import { CSSProperties, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useAchievementTypes, useReduxState } from 'hooks';
import { TitleWithIcon } from 'components/common';
import { CourseTitleWithInfo } from 'components/courses/common';

import type {
  Achievement,
  CalendarEvent,
  Competency,
  CompetencyLevel,
  Course,
  Group,
  IndividualAchievement,
  Integration,
  Path,
  PathStepItemAccessType
} from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  accessType?: PathStepItemAccessType;
  achievement?: Pick<Achievement, 'id' | 'title' | 'type'>;
  calendarEvent?: Pick<CalendarEvent, 'id' | 'title'>;
  children?: ReactNode;
  competency?: Pick<Competency, 'id' | 'name' | 'nameLocales'> & {
    level?: Pick<CompetencyLevel, 'id' | 'name' | 'nameLocales'>;
  };
  course?: Partial<
    Pick<
      Course,
      'id' | 'languageVersions' | 'picture' | 'picturePosition' | 'slug'
    >
  > &
    Pick<Course, 'title'>;
  disableLink?: boolean;
  group?: Pick<Group, 'id' | 'title'>;
  individualAchievement?: Pick<IndividualAchievement, 'id' | 'title'>;
  integration?: Pick<Integration, 'id' | 'title'>;
  linkHref?: string;
  linkToAdminPage?: boolean;
  onClick?: () => void;
  path?: Pick<Path, 'id' | 'title'>;
  preferredLanguageVersion?: string;
  showTooltip?: boolean;
  style?: CSSProperties;
  titleStyle?: CSSProperties;
  userId?: number;
}

const ProductLinkWithIcon = ({
  accessType,
  achievement,
  calendarEvent,
  children,
  competency,
  course,
  disableLink = false,
  group,
  individualAchievement,
  integration,
  linkHref,
  linkToAdminPage = false,
  onClick,
  path,
  preferredLanguageVersion,
  showTooltip = false,
  style,
  titleStyle,
  userId
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const router = useRouter();

  const achievementTypes = useAchievementTypes();

  const category = achievement
    ? 'achievement'
    : calendarEvent
    ? 'calendarEvent'
    : competency
    ? 'competency'
    : course
    ? 'course'
    : group
    ? 'group'
    : individualAchievement
    ? 'individualAchievement'
    : integration
    ? 'integration'
    : path
    ? 'path'
    : null;

  const icon = {
    achievement: achievementTypes[achievement?.type]?.icon,
    calendarEvent: ['far', 'calendar-days'] as IconProp,
    competency: 'medal' as const,
    course: 'book-open' as const,
    group: 'users' as const,
    individualAchievement: 'star' as const,
    integration: 'share-alt' as const,
    path: 'tasks' as const
  }[category];

  const isProfilePage = router?.pathname?.includes('/profile/') || false;

  const adminLinks: Record<typeof category, string | null> = {
    achievement: `/admin/achievements/${achievement?.id}`,
    calendarEvent: `/calendar/${calendarEvent?.id}`,
    competency: null,
    course: `/admin/courses/${course?.slug}`,
    group: `/groups/${group?.id}`,
    individualAchievement: null,
    integration: null,
    path: `/admin/paths/${path?.id}`
  };

  const supervisorLinks: Record<typeof category, string | null> = {
    achievement: `/users/${userId}/achievements/${achievement?.id}`,
    calendarEvent: `/calendar/${calendarEvent?.id}`,
    competency: null,
    course: `/users/${userId}/courses/${course?.id}`,
    group: `/groups/${group?.id}`,
    individualAchievement: null,
    integration: null,
    path: `/users/${userId}/paths/${path?.id}`
  };

  const userLinks: Record<typeof category, string | null> = {
    achievement: `/achievements/${achievement?.id}`,
    calendarEvent: `/calendar/${calendarEvent?.id}`,
    competency: null,
    course: `/courses/${course?.slug}`,
    group: null,
    individualAchievement: null,
    integration: null,
    path: `/paths/${path?.id}`
  };

  const shouldLinkToAdmin =
    linkToAdminPage ||
    accessType === 'owner' ||
    accessType === 'admin' ||
    accessType === 'supervisor';

  const shouldDisableLink = disableLink || accessType === 'none';

  const link = shouldDisableLink
    ? null
    : linkHref
    ? linkHref
    : isProfilePage || !shouldLinkToAdmin
    ? userLinks[category]
    : accessType === 'supervisor' && userId
    ? supervisorLinks[category]
    : adminLinks[category];

  const title = {
    achievement: achievement?.title,
    calendarEvent: calendarEvent?.title,
    competency: `${competency?.nameLocales?.[language] || competency?.name}${
      competency?.level
        ? ` (${
            competency.level.nameLocales?.[language] || competency.level.name
          })`
        : ''
    }`,
    course: course?.title,
    group: group?.title,
    individualAchievement: individualAchievement?.title,
    integration: integration?.title,
    path: path?.title
  }[category];

  const tooltip = {
    achievement: achievementTypes[achievement?.type]?.title,
    calendarEvent: messages.eventCalendarEvent,
    competency: messages.commonCompetence,
    course: messages.commonCourse,
    group: messages.commonGroup,
    integration: messages.commonIntegration,
    path: messages.commonPath
  }[category];

  if (category === 'course' && 'picture' in course) {
    return (
      <CourseTitleWithInfo
        course={course as Pick<Course, 'picture' | 'title'>}
        iconTooltip={tooltip}
        linkHref={link}
        onClick={onClick}
        pictureStyle={{ height: 22, width: 22 }}
        preferredLanguageVersion={preferredLanguageVersion}
        style={style}
        titleStyle={{
          color: theme.textColorLight,
          fontSize: '1.5rem',
          marginTop: 4,
          ...titleStyle
        }}
      >
        {children}
      </CourseTitleWithInfo>
    );
  }

  return (
    <TitleWithIcon
      avatarProps={{ fontSize: '1.1rem', padding: '6px 0', size: '24px' }}
      icon={icon}
      iconTooltip={showTooltip ? tooltip : null}
      onClick={onClick}
      style={style}
      title={title}
      titleStyle={{ fontSize: '1.5rem', marginTop: 4, ...titleStyle }}
      {...(link ? { titleLink: { href: link } } : {})}
    >
      {children}
    </TitleWithIcon>
  );
};

export default ProductLinkWithIcon;
